/* iter55g15 — Chapter paragraph-mutation hook.
 *
 * `saveParagraphEdit` and `mergeParagraphUp` are pure business
 * transforms on the chapter's text array — they build a new content
 * string and PUT it via chaptersApi.
 *
 * ⚠️  Contract kept identical to the previous inline implementation in
 * EditorPanel.jsx. The tricky bits are:
 *
 *   • Paste-fidelity — split ONLY on `\r?\n\r?\n+`. Single `\n` is an
 *     intra-paragraph line break, preserved for `white-space: pre-wrap`.
 *   • Non-breaking-space safety — trim only ASCII whitespace so we don't
 *     eat U+00A0 word-processor tab indents on save.
 *   • Empty-chapter first-write — when the chapter is empty the
 *     `paragraphs` prop contains SEED_FALLBACK placeholders; we must
 *     drop them (not carry them into the saved doc).
 *
 * Regressions here directly bring back the six-iteration cut/paste
 * editor bug. Do NOT loosen these rules without a Playwright test.
 *
 * Creative Interaction Tracking (Copyright Evidence)
 * ---------------------------------------------------
 * After every successful save, a fire-and-forget POST records the
 * before/after paragraph text as a creative interaction with an
 * immutable server-side timestamp. This runs silently — it never
 * blocks the save path or surfaces errors to the author.
 */
import { useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { chaptersApi, creativeInteractionApi } from '@/lib/api';

const asciiTrim = (s) => String(s || '').replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, '');

/**
 * Fire-and-forget creative interaction record.
 * Never throws — errors are swallowed so the save path is unaffected.
 */
async function _recordInteraction(projectId, chapterId, payload) {
  if (!projectId || !chapterId) return;
  try {
    if (payload.scope === 'paragraph_edit' || payload.scope === 'paragraph_merge') {
      await creativeInteractionApi.recordParagraphEdit(projectId, chapterId, payload);
    }
  } catch {
    // intentionally silent
  }
}

export function useChapterMutations({ chapter, paragraphs, isEmpty, setChapters, reportSave, projectId }) {
  // Snapshot for one-level cross-paragraph undo
  const lastDeleteSnapshotRef = useRef(null);

  const saveParagraphEdit = useCallback(async (idxInChapter, nextText) => {
    if (!chapter) {
      // No chapter to save to — prompt the writer to create one.
      window.dispatchEvent(new CustomEvent('loom:save-as-needed', { detail: { content: nextText || '' } }));
      return;
    }
    const parts = String(nextText || '')
      .split(/\r?\n\r?\n+/)     // paragraph breaks only
      .map(asciiTrim)
      .filter(Boolean);
    if (parts.length === 0) return;
    const currentParas = isEmpty ? [] : paragraphs.map((p) => p.text);
    // No-op when the single-paragraph case is unchanged.
    if (!isEmpty && parts.length === 1 && parts[0] === currentParas[idxInChapter]) return;

    // Capture before text BEFORE the async save
    const beforeText = currentParas[idxInChapter] || '';

    const before  = currentParas.slice(0, idxInChapter);
    const after   = currentParas.slice(idxInChapter + 1);
    const rebuilt = [...before, ...parts, ...after]
      .filter(Boolean)
      .join('\n\n');
    const doSave = async () => {
      const updated = await chaptersApi.update(chapter.id, { content: rebuilt });
      setChapters?.((prev) => prev.map((c) => (c.id === chapter.id
        ? { ...c, content: updated.content, words: updated.words }
        : c)));
      return updated;
    };
    try {
      if (reportSave) await reportSave(doSave(), 'paragraph-edit');
      else await doSave();

      // Silent creative interaction record (after successful save)
      const afterText = parts[0] || '';
      _recordInteraction(projectId, chapter.id, {
        scope:           'paragraph_edit',
        chapter_title:   chapter.title || '',
        paragraph_index: idxInChapter,
        before_text:     beforeText,
        after_text:      afterText,
      });
    } catch (e) {
      // reportSave already surfaces the error via saveState; fall back to
      // a toast when it isn't wired.
      if (!reportSave) toast.error(e?.response?.data?.detail || 'Could not save your edit.');
    }
  }, [chapter, paragraphs, isEmpty, setChapters, reportSave, projectId]);

  const mergeParagraphUp = useCallback(async (idxInChapter, currentText) => {
    if (!chapter || idxInChapter <= 0) return;
    const currentParas = isEmpty ? [] : paragraphs.map((p) => p.text);
    if (currentParas.length < 2) return;
    const prev   = currentParas[idxInChapter - 1] || '';
    const curr   = String(currentText ?? currentParas[idxInChapter] ?? '').trim();
    const joiner = prev.endsWith(' ') || !curr ? '' : ' ';
    const merged = (prev + joiner + curr).trim();

    // Capture before text for the two paragraphs being merged
    const beforeText = curr;  // the paragraph being merged upward

    const rebuilt = [
      ...currentParas.slice(0, idxInChapter - 1),
      merged,
      ...currentParas.slice(idxInChapter + 1),
    ].filter(Boolean).join('\n\n');
    const doMerge = async () => {
      const updated = await chaptersApi.update(chapter.id, { content: rebuilt });
      setChapters?.((prev2) => prev2.map((c) => (c.id === chapter.id
        ? { ...c, content: updated.content, words: updated.words }
        : c)));
      return updated;
    };
    try {
      if (reportSave) await reportSave(doMerge(), 'paragraph-merge');
      else await doMerge();

      // Silent creative interaction record
      _recordInteraction(projectId, chapter.id, {
        scope:           'paragraph_merge',
        chapter_title:   chapter.title || '',
        paragraph_index: idxInChapter - 1,
        before_text:     beforeText,
        after_text:      merged,
      });
    } catch (e) {
      if (!reportSave) toast.error(e?.response?.data?.detail || 'Could not merge paragraphs.');
    }
  }, [chapter, paragraphs, isEmpty, setChapters, reportSave, projectId]);

  // ── Cross-paragraph delete (Select All → Delete, or dragged selection) ──
  const crossParaDelete = useCallback(async (firstIdx, newFirstParaText, lastIdx) => {
    if (!chapter) return;
    const currentParas = isEmpty ? [] : paragraphs.map((p) => p.text);

    // Persist snapshot for one-level undo (Ctrl+Z)
    lastDeleteSnapshotRef.current = { content: chapter.content || '', words: chapter.words || 0 };

    // Split newFirstParaText on double-newlines (pasted content may contain them)
    const mergedParts = String(newFirstParaText || '')
      .split(/\r?\n\r?\n+/)
      .map(asciiTrim)
      .filter(Boolean);

    const newParas = [
      ...currentParas.slice(0, firstIdx),
      ...mergedParts,
      ...currentParas.slice(lastIdx + 1),
    ].filter(Boolean);

    const rebuilt = newParas.join('\n\n');
    const doSave = async () => {
      const updated = await chaptersApi.update(chapter.id, { content: rebuilt });
      setChapters?.((prev) => prev.map((c) => (c.id === chapter.id
        ? { ...c, content: updated.content, words: updated.words }
        : c)));
      return updated;
    };
    try {
      if (reportSave) await reportSave(doSave(), 'cross-para-delete');
      else await doSave();
    } catch (e) {
      if (!reportSave) toast.error(e?.response?.data?.detail || 'Could not save deletion.');
    }
  }, [chapter, paragraphs, isEmpty, setChapters, reportSave]);

  // ── Undo the last cross-paragraph delete ─────────────────────────────────
  const undoCrossParaDelete = useCallback(async () => {
    const snap = lastDeleteSnapshotRef.current;
    if (!snap || !chapter) return;
    lastDeleteSnapshotRef.current = null;
    try {
      const updated = await chaptersApi.update(chapter.id, { content: snap.content });
      setChapters?.((prev) => prev.map((c) => (c.id === chapter.id
        ? { ...c, content: updated.content, words: updated.words || snap.words }
        : c)));
      toast.success('Deletion undone.');
    } catch {
      toast.error('Could not undo deletion.');
    }
  }, [chapter, setChapters]);

  return { saveParagraphEdit, mergeParagraphUp, crossParaDelete, undoCrossParaDelete };
}